import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDemoTheme } from './DemoThemeContext';

const PROCESSING_STEPS = [
  { id: 1, label: 'Analyzing responses', duration: 800 },
  { id: 2, label: 'Evaluating coding submissions', duration: 900 },
  { id: 3, label: 'Generating performance insights', duration: 800 },
  { id: 4, label: 'Building analytics dashboard', duration: 700 },
  { id: 5, label: 'Assessment complete', duration: 500 },
];

export default function AIProcessing({ onComplete }) {
  const { isDark } = useDemoTheme();
  const [completedSteps, setCompletedSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    let stepIndex = 0;
    let cumulativeDelay = 400;
    PROCESSING_STEPS.forEach((step, i) => {
      const showAt = cumulativeDelay;
      const completeAt = cumulativeDelay + step.duration;
      cumulativeDelay = completeAt + 120;
      setTimeout(() => setCurrentStep(i), showAt);
      setTimeout(() => {
        setCompletedSteps((prev) => [...prev, i]);
        if (i === PROCESSING_STEPS.length - 1) {
          setTimeout(() => { setFinished(true); setTimeout(onComplete, 500); }, 600);
        }
      }, completeAt);
    });
  }, [onComplete]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center" style={{ background: 'var(--d-bg)' }}>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="flex flex-col items-center w-full max-w-sm px-6"
      >
        {/* Orb */}
        <div className="relative mb-10">
          <motion.div animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.6, 0.3] }} transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute inset-0 rounded-full blur-xl" style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.6) 0%, transparent 70%)' }} />
          <div className="relative w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)', boxShadow: '0 0 32px rgba(124,58,237,0.5)' }}>
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
              className="w-7 h-7 border-2 border-white/30 border-t-white rounded-full" />
          </div>
        </div>

        <h2 className="font-semibold text-xl mb-1" style={{ color: 'var(--d-text-hi)' }}>Evaluating Assessment</h2>
        <p className="text-sm mb-8" style={{ color: 'var(--d-text-2)' }}>AI is processing your responses</p>

        <div className="w-full space-y-3">
          {PROCESSING_STEPS.map((step, i) => {
            const isCompleted = completedSteps.includes(i);
            const isCurrent = currentStep === i && !isCompleted;
            return (
              <motion.div key={step.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }} className="flex items-center gap-3 font-mono text-xs">
                <AnimatePresence mode="wait">
                  {isCompleted ? (
                    <motion.span key="check" initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-4 text-emerald-400 flex-shrink-0">✓</motion.span>
                  ) : isCurrent ? (
                    <motion.div key="spin" className="w-3 h-3 rounded-full border border-purple-400/40 border-t-purple-400 flex-shrink-0" animate={{ rotate: 360 }} transition={{ duration: 0.7, repeat: Infinity, ease: 'linear' }} />
                  ) : (
                    <span key="dot" className="w-4 flex-shrink-0" style={{ color: 'var(--d-text-3)' }}>○</span>
                  )}
                </AnimatePresence>
                <span style={{ color: isCompleted ? 'var(--d-text-2)' : isCurrent ? 'var(--d-text-hi)' : 'var(--d-text-3)', transition: 'color 0.3s' }}>
                  {step.label}
                  {isCurrent && <motion.span animate={{ opacity: [0, 1, 0] }} transition={{ duration: 1.2, repeat: Infinity }}>...</motion.span>}
                </span>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
}
